import { fv } from "financial";
import { YearlyInvestmentSummary, InvestmentSummaryWithFees } from "../types/investment-summary";

export const numberWithCommas = (value: number): string => {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const addNisSign = (value: string): string => {
  return "₪" + value.toString();
};

export const calculateCompoundInterestWithMonthlyContributions = (
  principal: number,
  annualInterestRate: number,
  monthlyDeposit: number,
  numberOfYears: number,
  managementFeesEnabled: boolean = false,
  depositFeesPercentage: number = 0,
  accumulationFeesPercentage: number = 0
): InvestmentSummaryWithFees => {
  const monthlyInterestRate = annualInterestRate / 12 / 100;
  const investmentSummaries: YearlyInvestmentSummary[] = [];

  let balance = principal;
  let balanceWithoutFees = principal;
  let totalDepositFees = 0;
  let totalAccumulationFees = 0;
  let year = new Date().getFullYear();

  if (numberOfYears === 0) {
    investmentSummaries.push({
      year,
      deposits: "0",
      earnings: "0",
      balance: `${principal}`,
      balanceWithoutFees: `${principal}`,
    });
  }
  
  for (let yearIndex = 1; yearIndex <= numberOfYears; yearIndex++) {
    const beginingOfYearBalance = balance;
    const beginingOfYearBalanceWithoutFees = balanceWithoutFees;
    
    // Calculate monthly deposit after fees
    const monthlyDepositAfterFees = managementFeesEnabled 
      ? monthlyDeposit * (1 - depositFeesPercentage / 100)
      : monthlyDeposit;
    
    // Calculate deposit fees for this year
    if (managementFeesEnabled && depositFeesPercentage > 0) {
      const yearlyDepositFees = monthlyDeposit * 12 * (depositFeesPercentage / 100);
      totalDepositFees += yearlyDepositFees;
    }
    
    // Calculate balance without fees (for comparison)
    balanceWithoutFees = fv(
      monthlyInterestRate,
      12,
      monthlyDeposit * -1,
      beginingOfYearBalanceWithoutFees * -1
    );
    
    // Calculate balance with fees - now applying accumulation fees monthly
    if (managementFeesEnabled && accumulationFeesPercentage > 0) {
      // Manual month-by-month calculation with monthly accumulation fees
      const monthlyAccumulationFeeRate = accumulationFeesPercentage / 12 / 100;
      
      for (let month = 1; month <= 12; month++) {
        // Add monthly deposit
        balance += monthlyDepositAfterFees;
        
        // Calculate and add monthly interest
        const monthlyInterest = balance * monthlyInterestRate;
        balance += monthlyInterest;
        
        // Deduct monthly accumulation fee from total balance
        const monthlyAccumulationFee = balance * monthlyAccumulationFeeRate;
        totalAccumulationFees += monthlyAccumulationFee;
        balance -= monthlyAccumulationFee;
      }
    } else {
      // Standard calculation without monthly accumulation fees
      balance = fv(
        monthlyInterestRate,
        12,
        monthlyDepositAfterFees * -1,
        beginingOfYearBalance * -1
      );
    }

    const earnings = balance - (beginingOfYearBalance + monthlyDepositAfterFees * 12);

    investmentSummaries.push({
      year,
      deposits: addNisSign(numberWithCommas(monthlyDepositAfterFees * 12)),
      earnings: addNisSign(numberWithCommas(Math.round(earnings))),
      balance: addNisSign(numberWithCommas(Math.round(balance))),
      balanceWithoutFees: addNisSign(numberWithCommas(Math.round(balanceWithoutFees))),
      // For charting purposes only
      yearlyEarnings: Math.round(earnings),
      totalEarnings: Math.round(balance),
      totalEarningsWithoutFees: Math.round(balanceWithoutFees),
    });
    year++;
  }

  return {
    summaries: investmentSummaries,
    totalDepositFees: Math.round(totalDepositFees),
    totalAccumulationFees: Math.round(totalAccumulationFees),
  };
};